import React, { Children } from 'react';
import { motion, Variants } from 'framer-motion';
import { cn } from '../../utils/cn';
interface CardProps {
  children: ReactNode;
  className?: string;
  variant?: 'default' | 'glass' | 'outline' | 'elevated' | 'flat' | 'premium' | 'gradient' | 'shimmer';
  padding?: 'none' | 'sm' | 'md' | 'lg';
  interactive?: boolean;
  onClick?: () => void;
  glow?: boolean;
  animateOnScroll?: boolean;
  staggerChildren?: boolean;
}
export function Card({
  children,
  className = '',
  variant = 'default',
  padding = 'md',
  interactive = false,
  onClick,
  glow = false,
  animateOnScroll = false,
  staggerChildren = false
}: CardProps) {
  const variantsMap = {
    default: 'bg-white border border-gray-100 shadow-soft-sm',
    glass: 'bg-white/80 backdrop-blur-lg border border-white/60 shadow-soft-md supports-[backdrop-filter]:bg-white/60',
    outline: 'bg-transparent border border-gray-200',
    elevated: 'bg-white shadow-soft-lg border border-gray-50',
    flat: 'bg-gray-50 border border-transparent',
    premium: 'bg-white/95 backdrop-blur-xl border border-white/40 shadow-soft-xl supports-[backdrop-filter]:bg-white/80 ring-1 ring-white/50',
    gradient: 'bg-gradient-to-br from-white to-gray-50 border border-white/60 shadow-soft-md',
    shimmer: 'bg-white border border-gray-100 shadow-soft-sm overflow-hidden relative'
  };
  const paddings = {
    none: 'p-0',
    sm: 'p-4',
    md: 'p-6 sm:p-8',
    lg: 'p-8 sm:p-10'
  };
  // Determine if the card should be interactive
  const isInteractive = interactive || !!onClick;
  const glowStyles = glow ? 'relative overflow-hidden before:absolute before:inset-0 before:bg-gradient-to-tr before:from-primary-500/5 before:via-transparent before:to-secondary-500/5 before:opacity-0 hover:before:opacity-100 before:transition-opacity before:duration-500' : '';
  const shimmerStyles = variant === 'shimmer' ? 'after:absolute after:inset-0 after:-translate-x-full after:animate-shimmer after:bg-gradient-shimmer' : '';
  const baseClasses = cn('rounded-2xl sm:rounded-3xl transition-all duration-300', variantsMap[variant], paddings[padding], isInteractive ? 'cursor-pointer hover:border-primary-200/50' : '', glowStyles, shimmerStyles, className);
  // Animation variants for scroll reveal
  const scrollVariants: Variants = {
    hidden: {
      opacity: 0,
      y: 20
    },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.6,
        ease: [0.21, 0.47, 0.32, 0.98],
        when: 'beforeChildren',
        staggerChildren: staggerChildren ? 0.1 : 0
      }
    }
  };
  const hoverVariants = isInteractive ? {
    hover: {
      y: -4,
      scale: 1.005,
      boxShadow: '0 12px 32px rgba(45, 74, 62, 0.12), 0 8px 16px rgba(45, 74, 62, 0.08)',
      borderColor: 'rgba(45, 74, 62, 0.15)',
      transition: {
        duration: 0.3,
        ease: [0.16, 1, 0.3, 1]
      }
    },
    tap: {
      scale: 0.98,
      transition: {
        duration: 0.1
      }
    }
  } : {};
  if (animateOnScroll || isInteractive) {
    return <motion.div className={baseClasses} onClick={onClick} initial={animateOnScroll ? 'hidden' : undefined} whileInView={animateOnScroll ? 'visible' : undefined} viewport={animateOnScroll ? {
      once: true,
      margin: '-50px'
    } : undefined} variants={scrollVariants} whileHover={isInteractive ? 'hover' : undefined} whileTap={isInteractive ? 'tap' : undefined}
    // @ts-ignore - framer motion types can be tricky with dynamic variants
    {...hoverVariants} data-id="element-5210">
        {children}
      </motion.div>;
  }
  // Static card (no motion wrapper for better performance if not needed)
  return <div className={baseClasses} onClick={onClick} data-id="element-5211">
      {children}
    </div>;
}